import React, {
	createContext,
	useCallback,
	useContext,
	useImperativeHandle,
	useMemo,
} from 'react';

import {modalManagerStore} from '@netx/core-components/ModalManager';
import {AssetExpiration} from '../../components/AssetExpiration';
import {useAssetExpiration} from '../../hooks';

export type AssetExpirationContextType = ReturnType<
	typeof useAssetExpiration
> & {
	expire(id: number): void;
};

const noop = (...args: any): any => {};

export const AssetExpirationContext = createContext<AssetExpirationContextType>(
	{
		expire: noop,
		expireWithDate: noop,
		expireWithMilliseconds: noop,
		unexpire: noop,
		subscribe: noop,
		working: false,
	},
);

export const useAssetExpirationProvider = () =>
	useContext(AssetExpirationContext);

export type AssetExpirationProviderRef =
	| React.RefObject<AssetExpirationContextType>
	| ((node: AssetExpirationContextType) => void);

export type AssetExpirationProviderChildrenProps = AssetExpirationContextType;

export type AssetExpirationProviderProps = {
	ref?: AssetExpirationProviderRef;
	children:
		| React.ReactNode
		| ((props: AssetExpirationProviderChildrenProps) => React.ReactNode);
};

export const AssetExpirationProviderPrimitive = React.forwardRef<
	AssetExpirationContextType,
	AssetExpirationProviderProps
>(({children}, ref) => {
	const assetExpiration = useAssetExpiration();

	const expire = useCallback((id: number) => {
		modalManagerStore.show('AssetExpiration', AssetExpiration, {
			onSubmit: async (values: any) => {
				if (!values.expirationDate) throw new Error('No date provided');
				await assetExpiration.expireWithDate(id, values.expirationDate);
				modalManagerStore.hide('AssetExpiration');
			},
		});
	}, []);

	const contextValues = useMemo(
		() => ({
			...assetExpiration,
			expire,
		}),
		[assetExpiration, expire],
	);

	useImperativeHandle(ref, () => contextValues, [contextValues]);

	return (
		<AssetExpirationContext.Provider value={contextValues}>
			{typeof children === 'function' ? children(contextValues) : children}
		</AssetExpirationContext.Provider>
	);
});

export const AssetExpirationProvider = React.memo(
	AssetExpirationProviderPrimitive,
);

export default AssetExpirationProvider;
